import { MicrophoneIcon } from "@heroicons/react/24/outline";
import { StopIcon } from "@heroicons/react/24/solid";
import {
    Dictation,
    DictationError,
    DictationErrorOrigin,
    DictationFailure,
    useDictation,
} from "hooks/use-dictation";
import { useIsTouchPrimary } from "hooks/use-is-touch-primary";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useFeature } from "store/selectors/hooks";
import { DictationSource } from "util/dictation-save-tracker";
import {
    applySpokenPunctuation,
    spliceDictatedText,
    SplicedText,
    TextSelection,
} from "util/dictation-text";
import {
    speechEnginePunctuates,
    usesMicrosoftSpeechService,
} from "util/speech-recognition";
import { handleEvent } from "util/user-events";
import { TextArea, TextAreaProps } from "./textarea";

interface DictationTextAreaProps extends Omit<
    TextAreaProps,
    "value" | "onChange" | "accessory"
> {
    value: string;
    onValueChange: (value: string) => void;
    onDictationEnd: (value: string) => void;
    source: DictationSource;
    characterLimit: number;
}

interface DictationSession {
    committed: string;
    selection: TextSelection;
    startedAt: number;
    characters: number;
}

const errorMessages: Record<DictationError, string> = {
    "permission-denied":
        "Microphone access is blocked. Allow it in your browser settings to dictate.",
    "no-microphone": "No microphone was found.",
    offline: "You're offline. Dictation needs an internet connection.",
    "speech-service-unreachable":
        "Dictation couldn't reach the speech service. Try again in a moment.",
    "no-speech": "Didn't hear anything. Try again.",
    unavailable: "Dictation isn't available right now.",
};

const edgeSpeechServiceMessage =
    "Edge couldn't reach Microsoft's speech service. Try again in a moment, or dictate in Chrome instead.";

const messageForError = (error: DictationError, userAgent: string): string =>
    error === "speech-service-unreachable" && usesMicrosoftSpeechService(userAgent)
        ? edgeSpeechServiceMessage
        : errorMessages[error];

const advisoryErrors: DictationError[] = ["no-speech"];

const originEventProperties = (origin: DictationErrorOrigin): Record<string, string> => {
    switch (origin.kind) {
        case "recognizer":
            return {
                errorOrigin: origin.kind,
                recognizerCode: origin.code,
                recognizerMessage: origin.message,
            };
        case "start-exception":
            return {
                errorOrigin: origin.kind,
                exceptionName: origin.name,
                exceptionMessage: origin.message,
            };
        case "repeated-silence":
            return { errorOrigin: origin.kind };
        default: {
            const exhaustiveCheck: never = origin;
            throw new Error(`Unhandled dictation error origin ${exhaustiveCheck}`);
        }
    }
};

const reportDictationFailure = (
    failure: DictationFailure,
    source: DictationSource,
): void => {
    const eventName =
        failure.category === "permission-denied"
            ? "dictation-permission-denied"
            : "dictation-error";
    handleEvent(eventName, {
        source,
        error: failure.category,
        ...originEventProperties(failure.origin),
    });
};

const keepText = (text: string): string => text;

const clampSelection = (selection: TextSelection, length: number): TextSelection => ({
    start: Math.min(selection.start, length),
    end: Math.min(selection.end, length),
});

export const DictationTextArea: React.FC<DictationTextAreaProps> = props => {
    const {
        value,
        onValueChange,
        onDictationEnd,
        source,
        characterLimit,
        placeholder,
        onBlur,
        onSelect,
        ...rest
    } = props;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const valueRef = useRef(value);
    const sessionRef = useRef<DictationSession | null>(null);
    const lastSelectionRef = useRef<TextSelection | null>(null);
    const pendingSelectionRef = useRef<TextSelection | null>(null);
    const dictationRef = useRef<Dictation | null>(null);
    const [stoppedAtLimit, setStoppedAtLimit] = useState(false);
    const isTouchPrimary = useIsTouchPrimary();
    const punctuate = useMemo(
        () =>
            speechEnginePunctuates(navigator.userAgent)
                ? keepText
                : applySpokenPunctuation,
        [],
    );

    const showTranscript = useCallback(
        (session: DictationSession, text: string): SplicedText => {
            const spliced = spliceDictatedText(
                session.committed,
                session.selection,
                punctuate(text),
            );
            pendingSelectionRef.current = spliced.selection;
            onValueChange(spliced.value);
            if (spliced.value.length > characterLimit) {
                setStoppedAtLimit(true);
                dictationRef.current?.stop();
            }
            return spliced;
        },
        [characterLimit, onValueChange, punctuate],
    );

    const isFeatureEnabled = useFeature("voiceDictation");
    const dictation = useDictation({
        lang: navigator.language || "en-US",
        onInterim: text => {
            const session = sessionRef.current;
            if (session) {
                showTranscript(session, text);
            }
        },
        onFinal: text => {
            const session = sessionRef.current;
            if (!session) {
                return;
            }
            const spliced = showTranscript(session, text);
            session.committed = spliced.value;
            session.selection = spliced.selection;
            session.characters += text.length;
        },
        onEnd: reason => {
            const session = sessionRef.current;
            sessionRef.current = null;
            if (!session) {
                return;
            }
            handleEvent("dictation-end", {
                source,
                reason,
                durationMs: Date.now() - session.startedAt,
                characters: session.characters,
            });
            if (session.characters > 0) {
                onDictationEnd(valueRef.current);
            }
        },
    });

    useEffect(() => {
        dictationRef.current = dictation;
    }, [dictation]);

    useLayoutEffect(() => {
        valueRef.current = value;
    }, [value]);

    useLayoutEffect(() => {
        const pending = pendingSelectionRef.current;
        const textarea = textareaRef.current;
        pendingSelectionRef.current = null;
        if (!pending || !textarea) {
            return;
        }
        if (document.activeElement === textarea) {
            textarea.setSelectionRange(pending.start, pending.end);
        }
        if (pending.end === textarea.value.length) {
            textarea.scrollTop = textarea.scrollHeight;
        }
    }, [value]);

    useEffect(() => {
        if (dictation.failure) {
            reportDictationFailure(dictation.failure, source);
        }
    }, [dictation.failure, source]);

    const currentSelection = useCallback((): TextSelection => {
        const textarea = textareaRef.current;
        const length = valueRef.current.length;
        if (textarea && document.activeElement === textarea) {
            return { start: textarea.selectionStart, end: textarea.selectionEnd };
        }
        return clampSelection(
            lastSelectionRef.current ?? { start: length, end: length },
            length,
        );
    }, []);

    const placeCaretForDesktop = useCallback(
        (selection: TextSelection) => {
            const textarea = textareaRef.current;
            if (isTouchPrimary || !textarea) {
                return;
            }
            textarea.focus();
            textarea.setSelectionRange(selection.start, selection.end);
        },
        [isTouchPrimary],
    );

    const startDictation = useCallback(() => {
        const selection = currentSelection();
        sessionRef.current = {
            committed: valueRef.current,
            selection,
            startedAt: Date.now(),
            characters: 0,
        };
        setStoppedAtLimit(false);
        handleEvent("dictation-start", { source });
        placeCaretForDesktop(selection);
        dictation.start();
    }, [currentSelection, dictation, placeCaretForDesktop, source]);

    const isListening = dictation.status === "listening";

    const toggleDictation = useCallback(() => {
        if (isListening) {
            dictation.stop();
        } else {
            startDictation();
        }
    }, [dictation, isListening, startDictation]);

    const rememberSelection = (target: HTMLTextAreaElement) => {
        lastSelectionRef.current = {
            start: target.selectionStart,
            end: target.selectionEnd,
        };
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        rememberSelection(e.currentTarget);
        onBlur?.(e);
    };

    const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
        rememberSelection(e.currentTarget);
        onSelect?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (isListening) {
            dictation.abort();
        }
        onValueChange(e.target.value);
    };

    const keepFocusInField = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
    };

    const isAvailable = isFeatureEnabled && dictation.isSupported && !rest.disabled;

    const micButton = isAvailable ? (
        <button
            type="button"
            onMouseDown={keepFocusInField}
            onClick={toggleDictation}
            aria-label="Dictate"
            aria-pressed={isListening}
            title="Dictate"
            className={`relative inline-flex items-center justify-center rounded-full p-2.5 sm:p-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                isListening
                    ? "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
                    : "text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus-visible:ring-brand-500"
            }`}>
            {isListening ? (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-full bg-red-400 opacity-60 animate-ping"
                />
            ) : null}
            {isListening ? (
                <StopIcon aria-hidden="true" className="relative w-5 h-5" />
            ) : (
                <MicrophoneIcon aria-hidden="true" className="relative w-5 h-5" />
            )}
        </button>
    ) : null;

    return (
        <div>
            <TextArea
                {...rest}
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onSelect={handleSelect}
                placeholder={
                    placeholder ??
                    (isAvailable ? "Type or use the mic to dictate" : undefined)
                }
                accessory={micButton}
            />
            {isAvailable ? (
                <div aria-live="polite" className="text-xs">
                    <DictationStatus
                        isListening={isListening}
                        error={dictation.failure?.category ?? null}
                        stoppedAtLimit={stoppedAtLimit}
                    />
                </div>
            ) : null}
        </div>
    );
};

interface DictationStatusProps {
    isListening: boolean;
    error: DictationError | null;
    stoppedAtLimit: boolean;
}

const DictationStatus: React.FC<DictationStatusProps> = props => {
    const { isListening, error, stoppedAtLimit } = props;
    if (isListening) {
        return (
            <span className="mt-1 inline-flex items-center gap-1.5 text-gray-600">
                <span
                    aria-hidden="true"
                    className="inline-flex h-2 w-2 rounded-full bg-red-500"
                />
                Listening… press the stop button when you're done.
            </span>
        );
    }
    if (error) {
        const tone = advisoryErrors.includes(error) ? "text-gray-600" : "text-red-600";
        return (
            <span className={`mt-1 inline-block ${tone}`}>
                {messageForError(error, navigator.userAgent)}
            </span>
        );
    }
    if (stoppedAtLimit) {
        return (
            <span className="mt-1 inline-block text-gray-600">
                Dictation stopped because this note is over the character limit.
            </span>
        );
    }
    return null;
};
